import React, { useState, useEffect } from 'react';
import { BarChart2, List, ShieldAlert, Activity, Users, Send } from 'lucide-react';
import { getAuthorizedHeaders } from '../utils/authorizedHeaders';
import EmailStatsPanel from './EmailStatsPanel';

//const API_BASE = 'http://localhost:3001';
const API_BASE = 'https://warngen-api-352514293915.europe-west1.run.app';

const Informes = ({ usuario }) => {
    const [activeTab, setActiveTab] = useState('estadisticas');
    const [cargando, setCargando] = useState(true);
    const [selectedAlerta, setSelectedAlerta] = useState(null);
    
    const [estadisticas, setEstadisticas] = useState([]);
    const [alertas, setAlertas] = useState([]);
    const [logs, setLogs] = useState([]);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        setCargando(true);
        try {
            const [resEst, resAle, resLogs] = await Promise.all([
                fetch(`${API_BASE}/api/informes/estadisticas`, { headers: getAuthorizedHeaders() }),
                fetch(`${API_BASE}/api/informes/alertas`, { headers: getAuthorizedHeaders() }),
                fetch(`${API_BASE}/api/informes/logs`, { headers: getAuthorizedHeaders() })
            ]);
            
            if (resEst.ok) setEstadisticas(await resEst.json());
            if (resAle.ok) setAlertas(await resAle.json());
            if (resLogs.ok) setLogs(await resLogs.json());
            
        } catch (error) {
            console.error('Error cargando informes:', error);
        } finally {
            setCargando(false);
        }
    };

    // --- RENDERIZADO DE PESTAÑAS ---

    const renderEstadisticas = () => {
        const mesActual = estadisticas[0];

        return (
            <div className="space-y-8">
                <EmailStatsPanel />

                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Todos los canales (registrado en WarnGen)</h3>
                    {estadisticas.length === 0 ? (
                        <p className="text-gray-500 text-center py-6">Todavía no hay envíos registrados en los últimos 12 meses.</p>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-blue-600 font-semibold mb-1">Mensajes enviados ({mesActual.mes}/{mesActual.anio})</p>
                                        <p className="text-3xl font-bold text-blue-900">{mesActual.total_enviados}</p>
                                    </div>
                                    <Send size={32} className="text-blue-300" />
                                </div>
                                <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                                    <p className="text-sm text-purple-600 font-semibold mb-1">Por Email</p>
                                    <p className="text-2xl font-bold text-purple-900">{mesActual.emails}</p>
                                </div>
                                <div className="bg-orange-50 p-4 rounded-lg border border-orange-100">
                                    <p className="text-sm text-orange-600 font-semibold mb-1">Por SMS</p>
                                    <p className="text-2xl font-bold text-orange-900">{mesActual.sms}</p>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                                    <p className="text-sm text-green-600 font-semibold mb-1">Por WhatsApp</p>
                                    <p className="text-2xl font-bold text-green-900">{mesActual.whatsapp}</p>
                                </div>
                            </div>

                            <div className="overflow-x-auto bg-white rounded-lg border">
                                <table className="w-full text-sm text-left text-gray-700">
                                    <thead className="bg-gray-50 text-gray-900 font-semibold">
                                        <tr>
                                            <th className="px-6 py-3">Mes/Año</th>
                                            <th className="px-6 py-3 text-right">Comunicaciones</th>
                                            <th className="px-6 py-3 text-right">Mensajes OK</th>
                                            <th className="px-6 py-3 text-right">Fallidos</th>
                                            <th className="px-6 py-3 text-right">Emails</th>
                                            <th className="px-6 py-3 text-right">SMS</th>
                                            <th className="px-6 py-3 text-right">WhatsApp</th>
                                            <th className="px-6 py-3 text-right">Tasa Éxito</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {estadisticas.map((est, index) => (
                                            <tr key={index} className="border-b hover:bg-gray-50">
                                                <td className="px-6 py-4">{est.mes}/{est.anio}</td>
                                                <td className="px-6 py-4 text-right">{est.total_alertas}</td>
                                                <td className="px-6 py-4 text-right font-medium">{est.total_enviados}</td>
                                                <td className="px-6 py-4 text-right text-red-600">{est.total_fallidos}</td>
                                                <td className="px-6 py-4 text-right">{est.emails}</td>
                                                <td className="px-6 py-4 text-right">{est.sms}</td>
                                                <td className="px-6 py-4 text-right">{est.whatsapp}</td>
                                                <td className="px-6 py-4 text-right text-green-600">{est.tasa_exito ?? 0}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const renderHistorialAlertas = () => {
        if (alertas.length === 0) return <p className="text-gray-500 text-center py-8">No hay historial de alertas registradas.</p>;
        
        return (
            <div className="overflow-x-auto bg-white rounded-lg border">
                <table className="w-full text-sm text-left text-gray-700">
                    <thead className="bg-gray-50 text-gray-900 font-semibold">
                        <tr>
                            <th className="px-6 py-3">ID / Fecha</th>
                              <th className="px-6 py-3">Campaña / Plantilla</th>
                            <th className="px-6 py-3">Plantilla</th>
                            <th className="px-6 py-3">Canal</th>
                            <th className="px-6 py-3">Estado</th>
                            <th className="px-6 py-3">Destinatarios</th>
                        </tr>
                    </thead>
                    <tbody>
                        {alertas.map(alerta => (
                            <tr key={alerta.id} onClick={() => setSelectedAlerta(alerta)} className="border-b hover:bg-gray-50 cursor-pointer transition-colors" title="Ver detalles y analíticas">
                                <td className="px-6 py-4">
                                    <span className="font-semibold block">#{alerta.id}</span>
                                    <span className="text-xs text-gray-500">{new Date(alerta.fecha_enviada || alerta.fecha_creacion).toLocaleString()}</span>
                                </td>
                                <td className="px-6 py-4">
                                    {alerta.plantilla_nombre} 
                                </td>
                                <td className="px-6 py-4 capitalize">{alerta.canal}</td>
                                <td className="px-6 py-4">
                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                        alerta.estado === 'enviada' ? 'bg-green-100 text-green-800' :
                                        alerta.estado === 'programada' ? 'bg-blue-100 text-blue-800' :
                                        'bg-red-100 text-red-800'
                                    }`}>
                                        {alerta.estado}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-1">
                                        <Users size={14} className="text-gray-400" />
                                        <span>{alerta.total_destinatarios}</span>
                                        {alerta.total_destinatarios > 0 && (
                                            <span className="text-xs ml-2 text-green-600">
                                                ({alerta.tasa_exito || 0}%)
                                            </span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    const renderAuditoria = () => {
        if (logs.length === 0) return <p className="text-gray-500 text-center py-8">No hay registros de actividad todavía.</p>;
        
        return (
             <div className="overflow-x-auto bg-white rounded-lg border">
                <table className="w-full text-sm text-left text-gray-700">
                    <thead className="bg-gray-50 text-gray-900 font-semibold">
                        <tr>
                            <th className="px-6 py-3">Fecha y Hora</th>
                            <th className="px-6 py-3">Usuario</th>
                            <th className="px-6 py-3">Módulo</th>
                            <th className="px-6 py-3">Actividad</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map(log => (
                            <tr key={log.id} className="border-b hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {new Date(log.fecha_creacion).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 font-medium">
                                    {log.usuario_nombre || 'Sistema'}
                                </td>
                                <td className="px-6 py-4 capitalize">
                                    {log.modulo}
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-2">
                                        <span className={`w-2 h-2 rounded-full ${
                                            log.tipo === 'info' ? 'bg-blue-500' :
                                            log.tipo === 'warning' ? 'bg-yellow-500' :
                                            log.tipo === 'error' ? 'bg-red-500' : 'bg-gray-500'
                                        }`}></span>
                                        {log.mensaje}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100">Centro de Informes</h2>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                {/* Tabs NavBar */}
                <div className="flex border-b dark:border-gray-700 overflow-x-auto">
                    <button 
                        onClick={() => setActiveTab('estadisticas')}
                        className={`flex-1 sm:flex-initial justify-center flex items-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-base font-medium whitespace-nowrap transition-colors ${activeTab === 'estadisticas' ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50 dark:bg-primary-900/20' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                    >
                        <BarChart2 size={18} className="shrink-0" />
                        Estadísticas
                    </button>
                    <button 
                        onClick={() => setActiveTab('historial')}
                        className={`flex-1 sm:flex-initial justify-center flex items-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-base font-medium whitespace-nowrap transition-colors ${activeTab === 'historial' ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50 dark:bg-primary-900/20' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                    >
                        <List size={18} className="shrink-0" />
                        Historial Alertas
                    </button>
                    <button 
                         onClick={() => setActiveTab('auditoria')}
                        className={`flex-1 sm:flex-initial justify-center flex items-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-base font-medium whitespace-nowrap transition-colors ${activeTab === 'auditoria' ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50 dark:bg-primary-900/20' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                    >
                        <Activity size={18} className="shrink-0" />
                        Auditoría
                    </button>
                </div>

                {/* Content Area */}
                <div className="p-4 sm:p-6">
                    {cargando ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                            <span className="ml-3 text-gray-600 font-medium">Cargando informes...</span>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'estadisticas' && renderEstadisticas()}
                            {activeTab === 'historial' && renderHistorialAlertas()}
                            {activeTab === 'auditoria' && renderAuditoria()}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Informes;
